import FirebaseHandlerBase from "../firebase_handler_base";
import {SYSTEM_EVENTS} from "../../../../events/events";
import Event from '../../../../utils/event';
import _ from "../../../../utils/underscore";
import {FP_VOUCHER_TYPES} from "../../../../../../common/const/free_play_consts";
import {IExpirationDate, ISubscriptionParams} from "../firebase_const";
import {
  IFreePlayVoucherData,
  IFreePlayVoucherDelta
} from "../../../../../../common/interfaces/free-play/free-play-voucher-data.interface";
import {Check} from "@sparkware/app-utils";


enum TYPES{
  ADDED = "added",
  REMOVED = "removed",
  MODIFIED = "modified"
}

interface FPVoucherData{
  CurrentBalance: number;
  ExpirationDate: IExpirationDate;
  FreePlayId: number;
  FreePlayTypeId: number;
  GameTypeIdList: number[];
  MaxBetAmount: number;
  OriginAmount: number;
  UpdateCounter: number;
  Winnings: number;
  PendingDeletion?: boolean;
}

interface IFPReceivedVoucher{
  data: FPVoucherData;
  type: TYPES;
  id: string;
}

export default class FreeplayHandler extends FirebaseHandlerBase{
  static class: string = "FreeplayHandler";

  protected getSubscriptionParams(): ISubscriptionParams {
    return {
      eventName: 'freePlay',
      successCallback: this._updateFreePlay,
    };
  }

  protected getFeatureName(): string {
    return "FP";
  }

  protected getFeatureLabel(): string {
    return "FreePlay";
  }

  protected getFallbackServiceName(): string{
    return "Wallet";
  }

  private _updateFreePlay = (data: IFPReceivedVoucher[]) => {
    this.logger.debug('Firebase got FP message. Raw data: ' + JSON.stringify(data));

    if(data.length !== 0){
      const vouchersChanges = this._parseVouchers(data);


      if(vouchersChanges.removedVouchers.length > 0) {
        Event.fire(SYSTEM_EVENTS.SESSION_EVENT_FREE_PLAY_BATCH_REMOVED, vouchersChanges.removedVouchers);
      }

      if(vouchersChanges.addedVouchers.length > 0) {
        Event.fire(SYSTEM_EVENTS.SESSION_EVENT_FREE_PLAY_BATCH_ADDED, vouchersChanges.addedVouchers);
      }

      if(vouchersChanges.updatedVouchers.length > 0) {
        Event.fire(SYSTEM_EVENTS.SESSION_EVENT_FREE_PLAY_BATCH_UPDATED, vouchersChanges.updatedVouchers);
      }

      //TODO - remove this when free play from SDK will be removed
      vouchersChanges.removedVouchers.forEach(id => {
        Event.fire(SYSTEM_EVENTS.SESSION_EVENT_FREE_PLAY_REMOVED, id);
      });
    }

    this.serviceFactory.firebaseElasticService().sendMessageUpdate(this.getFeatureLabel(), JSON.stringify(data));
  }

  private _parseVouchers(freePlayData: IFPReceivedVoucher[]): {addedVouchers: IFreePlayVoucherData[]; updatedVouchers: IFreePlayVoucherDelta[]; removedVouchers: string[]}{
    const addedVouchers: IFreePlayVoucherData[] = [];
    const updatedVouchers: IFreePlayVoucherDelta[] = [];
    const removedVouchers: string[] = [];

    freePlayData.forEach((receivedVoucher: IFPReceivedVoucher) => {
      const receivedData: FPVoucherData = receivedVoucher.data;
      const voucherData: IFreePlayVoucherData = {
        ID: receivedData.FreePlayId.toString(), //make sure that even if it doesn't come as string from firebase we will make it string
        CurrentBalance: receivedData.CurrentBalance,
        Winnings: receivedData.Winnings,
        FreePlayTypeId: (receivedData.FreePlayTypeId?.toString() || FP_VOUCHER_TYPES.FreePlay) as FP_VOUCHER_TYPES,
        OriginAmount: receivedData.OriginAmount,
        CreationDate: null,
        ExpirationDate: receivedData.ExpirationDate ? new Date(1000*receivedData.ExpirationDate.seconds) : undefined,
        SpinBetAmount: receivedData.MaxBetAmount,
        GameTypeIDList: receivedData.GameTypeIdList,
        PendingDeletion: _.isNullOrUndefined(receivedData.PendingDeletion) ? false : _.toBoolean(receivedData.PendingDeletion)
      };
      switch(receivedVoucher.type) {
        case TYPES.ADDED:
          if(voucherData.PendingDeletion) {
            removedVouchers.push(voucherData.ID);
          }else{
            addedVouchers.push(voucherData);
          }
          break;
        case TYPES.MODIFIED:
          if(voucherData.PendingDeletion) {
             removedVouchers.push(voucherData.ID);
          } else {
            const modifiedVoucher: IFreePlayVoucherDelta = {
              ID: voucherData.ID
            }
            Object.keys(voucherData).forEach(propertyName => {
              if(!Check.isUndefined(voucherData[propertyName])) {
                modifiedVoucher[propertyName] = voucherData[propertyName];
              }
            });

            updatedVouchers.push(modifiedVoucher);
          }

          break;
        case TYPES.REMOVED:
          this.logger.debug(`Firebase got FP message. Remove voucher id: ${voucherData.ID}`);
          removedVouchers.push(voucherData.ID)
          break;
      }
    });

    return {
      addedVouchers: addedVouchers,
      updatedVouchers: updatedVouchers,
      removedVouchers: removedVouchers
    };
  }
}